// v16.61.3 (Feb 2026) — Popup hiển thị kết quả xét duyệt AI cho avatar
// upload (cả PASS và FAIL). Dữ liệu: confidence + reason từ backend
// (giống định dạng notify admin Telegram). User BUỘC bấm "Đóng" để về
// màn hình chính — muốn upload lại phải tự click avatar lần nữa (khi đó
// mới hiện consent + wizard hướng dẫn như thường).
import { useEffect, useState } from "react";
import { m as motion, AnimatePresence } from "framer-motion";
import { CheckCircle, XCircle, Brain, Info, X } from "@phosphor-icons/react";

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {"pass" | "fail" | "skipped"} props.verdict
 * @param {string | null} [props.reason]
 * @param {number | null} [props.confidence]  // 0.0 - 1.0
 * @param {number | null} [props.autoCloseSec] // v20.8 — tự gọi onClose sau N giây
 * @param {string | null} [props.closeLabel]   // v20.8 — override chữ nút chính
 */
export default function AvatarAIResultModal({
  open,
  onClose,
  verdict,
  reason,
  confidence,
  autoCloseSec = null,
  closeLabel = null,
}) {
  // v20.8 — Bug PROD 26/8: upload thành công nhưng modal không tự tắt → user
  // kẹt trong màn chặn, phải tắt app mở lại. Nay đếm ngược rồi tự onClose
  // (parent điều hướng về trang chủ).
  const [remain, setRemain] = useState(autoCloseSec);
  useEffect(() => {
    if (!open || !autoCloseSec) return undefined;
    setRemain(autoCloseSec);
    const id = setInterval(() => {
      setRemain((prev) => {
        const next = (prev ?? 0) - 1;
        if (next <= 0) {
          clearInterval(id);
          onClose?.();
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [open, autoCloseSec, onClose]);

  const isPass = verdict === "pass";
  const isSkipped = verdict === "skipped";
  const isFail = verdict === "fail";

  const accent = isPass
    ? { border: "border-emerald-400/45", glow: "shadow-[0_24px_80px_rgba(16,185,129,0.35)]", chip: "bg-emerald-500/15 border-emerald-400/40 text-emerald-200", icon: "text-emerald-300", title: "text-emerald-100" }
    : isFail
    ? { border: "border-rose-400/45", glow: "shadow-[0_24px_80px_rgba(244,63,94,0.35)]", chip: "bg-rose-500/15 border-rose-400/40 text-rose-200", icon: "text-rose-300", title: "text-rose-100" }
    : { border: "border-amber-400/45", glow: "shadow-[0_24px_80px_rgba(245,158,11,0.3)]", chip: "bg-amber-500/15 border-amber-400/40 text-amber-200", icon: "text-amber-300", title: "text-amber-100" };

  const HeaderIcon = isPass ? CheckCircle : isFail ? XCircle : Info;
  const headerLabel = isPass
    ? "Upload Avatar HỢP LỆ"
    : isFail
    ? "Upload Avatar bị TỪ CHỐI"
    : "Upload Avatar hoàn tất";

  const confPct =
    typeof confidence === "number" && Number.isFinite(confidence)
      ? Math.round(confidence * 100)
      : null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="pl-fixed-fullscreen z-[320] flex items-end sm:items-center justify-center bg-black/85 backdrop-blur-md px-3 pb-3 sm:pb-0"
          onClick={onClose}
          data-testid="avatar-ai-result-modal"
        >
          <motion.div
            initial={{ y: 32, scale: 0.96, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 32, scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.24 }}
            className={`w-full max-w-md rounded-3xl border ${accent.border} bg-gradient-to-br from-[#0F1623] via-[#0B1220] to-[#080D18] ${accent.glow} overflow-hidden flex flex-col`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-5 py-4 flex items-center gap-3 border-b border-white/10">
              <div className={`w-11 h-11 rounded-2xl border ${accent.border} bg-white/[0.03] flex items-center justify-center shrink-0`}>
                <HeaderIcon size={22} weight="fill" className={accent.icon} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-white/50">
                  Kết quả xét duyệt AI
                </div>
                <div className={`text-[15px] font-black leading-tight ${accent.title}`} data-testid="avatar-ai-result-title">
                  {headerLabel}
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="w-9 h-9 rounded-xl hover:bg-white/10 flex items-center justify-center text-white/55 hover:text-white transition shrink-0"
                aria-label="Đóng"
                data-testid="avatar-ai-result-close-x"
              >
                <X size={16} weight="bold" />
              </button>
            </div>

            {/* Body — Confidence + Reason (giống Telegram admin notify) */}
            <div className="px-5 py-5 space-y-3">
              {confPct !== null && (
                <div
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${accent.chip} text-[12.5px] font-bold`}
                  data-testid="avatar-ai-result-confidence"
                >
                  <Brain size={14} weight="fill" />
                  AI conf {(confidence).toFixed(2)}
                  <span className="opacity-70 font-semibold">({confPct}%)</span>
                </div>
              )}

              {isSkipped && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-400/40 bg-amber-500/12 text-amber-100 text-[12.5px] font-bold">
                  <Info size={14} weight="fill" />
                  AI đã bị bỏ qua theo lựa chọn của bạn
                </div>
              )}

              <div
                className="rounded-2xl border border-white/10 bg-white/[0.03] p-3.5 text-[13px] text-white/85 leading-relaxed"
                data-testid="avatar-ai-result-reason"
              >
                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-white/45 mb-1.5">
                  📋 Lý do
                </div>
                {reason || (isPass
                  ? "Ảnh chân dung phù hợp làm avatar."
                  : isSkipped
                  ? "Ảnh đã được tải trực tiếp lên hệ thống."
                  : "Ảnh không đạt yêu cầu — vui lòng chọn ảnh khác.")}
              </div>

              {isFail && (
                <div className="text-[11.5px] text-white/55 leading-snug pt-0.5">
                  💡 Muốn thử lại? Bấm vào ảnh đại diện lần nữa để bắt đầu từ đầu — hệ thống sẽ hiện lại hướng dẫn chọn ảnh phù hợp.
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 pb-5 pt-1">
              <button
                type="button"
                onClick={onClose}
                className={`w-full h-12 rounded-2xl font-black text-[14px] transition shadow-md ${
                  isPass
                    ? "bg-gradient-to-r from-emerald-500 to-emerald-400 hover:from-emerald-400 hover:to-emerald-300 text-white"
                    : isFail
                    ? "bg-gradient-to-r from-rose-500 to-rose-400 hover:from-rose-400 hover:to-rose-300 text-white"
                    : "bg-gradient-to-r from-amber-500 to-amber-400 hover:from-amber-400 hover:to-amber-300 text-white"
                }`}
                data-testid="avatar-ai-result-close"
              >
                {closeLabel
                  ? `${closeLabel}${autoCloseSec ? ` (${remain ?? autoCloseSec}s)` : ""}`
                  : isPass ? "Tuyệt vời, đóng lại" : "Đã hiểu, đóng lại"}
              </button>
              {!!autoCloseSec && (
                <div
                  className="mt-2 text-center text-[11.5px] text-white/50"
                  data-testid="avatar-ai-result-autoclose-hint"
                >
                  Tự động về trang chủ sau {remain ?? autoCloseSec}s…
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
